// ABOUTME: Provider owning the calendar's copy/paste state.
// ABOUTME: Tracks which activities are copied and their type for paste operations.

import React, { type ReactNode, useCallback, useMemo, useState } from "react"
import type { CalendarItem } from "@/graphql/main/gql/graphql"
import { ActivityType } from "@/graphql/main/gql/graphql"
import type { CopyContextProps } from "./useContextMenu"
import { CopyContext } from "./useContextMenu"

export const CopyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isCopyActivities, setIsCopyActivities] = useState({
    isCopyDayRations: false,
    isCopyWeekRations: true,
    isCopyDayWorkout: false,
    isCopyWeekWorkout: false,
    isCopyDayEvent: false,
    isCopyWeekEvent: false,
    isCopyDayRest: false,
    isCopyWeekRest: false,
  })
  const [copiedActivities, setCopiedActivities] = useState<CalendarItem[]>([])

  const handleCopy = useCallback<CopyContextProps["handleCopy"]>(
    (val, type, typeActivity) => {
      if (val) {
        setIsCopyActivities({
          isCopyDayRations: type === "day" && typeActivity === ActivityType.Menu,
          isCopyWeekRations: type === "week" && typeActivity === ActivityType.Menu,
          isCopyDayWorkout: type === "day" && typeActivity === ActivityType.Workout,
          isCopyWeekWorkout: type === "week" && typeActivity === ActivityType.Workout,
          isCopyDayEvent: type === "day" && typeActivity === ActivityType.Event,
          isCopyWeekEvent: type === "week" && typeActivity === ActivityType.Event,
          isCopyDayRest: type === "day" && typeActivity === ActivityType.Rest,
          isCopyWeekRest: type === "week" && typeActivity === ActivityType.Rest,
        })
      } else {
        setIsCopyActivities((prevState) => {
          const prevActive = Object.entries(prevState).find(([_key, value]) => value)
          const fieldWithTrue = Object.entries(isCopyActivities).find(([_key, value]) => value)
          if (prevActive && prevActive[0] === fieldWithTrue?.[0]) {
            return prevState
          }
          return {
            isCopyDayRations: false,
            isCopyWeekRations: false,
            isCopyDayWorkout: false,
            isCopyWeekWorkout: false,
            isCopyDayEvent: false,
            isCopyWeekEvent: false,
            isCopyDayRest: false,
            isCopyWeekRest: false,
          }
        })
      }
    },
    [isCopyActivities],
  )

  const handleCopyMulti = useCallback<CopyContextProps["handleCopyMulti"]>((val, type, activityTypes) => {
    if (val) {
      setIsCopyActivities({
        isCopyDayRations: type === "day" && activityTypes.includes(ActivityType.Menu),
        isCopyWeekRations: type === "week" && activityTypes.includes(ActivityType.Menu),
        isCopyDayWorkout: type === "day" && activityTypes.includes(ActivityType.Workout),
        isCopyWeekWorkout: type === "week" && activityTypes.includes(ActivityType.Workout),
        isCopyDayEvent: type === "day" && activityTypes.includes(ActivityType.Event),
        isCopyWeekEvent: type === "week" && activityTypes.includes(ActivityType.Event),
        isCopyDayRest: type === "day" && activityTypes.includes(ActivityType.Rest),
        isCopyWeekRest: type === "week" && activityTypes.includes(ActivityType.Rest),
      })
    } else {
      setIsCopyActivities({
        isCopyDayRations: false,
        isCopyWeekRations: false,
        isCopyDayWorkout: false,
        isCopyWeekWorkout: false,
        isCopyDayEvent: false,
        isCopyWeekEvent: false,
        isCopyDayRest: false,
        isCopyWeekRest: false,
      })
    }
  }, [])

  const value = useMemo(
    () => ({ copiedActivities, setCopiedActivities, isCopyActivities, handleCopy, handleCopyMulti }),
    [copiedActivities, isCopyActivities, handleCopy, handleCopyMulti],
  )

  return <CopyContext.Provider value={value}>{children}</CopyContext.Provider>
}
